import { lumi } from '@/lib/lumi'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { usePageMeta } from '@/hooks/usePageMeta'

const ACCENT = '#B9A3D9'
const ACCENT_SOFT = '#d4c5e8'
const GOLD = '#C9A96E'
const GOLD_SOFT = '#e0c898'

// ── Country selector (mesmo padrão do Step 1 e da Triagem) ─────────────────────
type CountryKey = 'BR' | 'PT' | 'US' | 'OTHER'

const COUNTRIES: { key: CountryKey; label: string; flag: string; code: string }[] = [
  { key: 'BR', label: 'Brasil', flag: '🇧🇷', code: '+55' },
  { key: 'PT', label: 'Portugal', flag: '🇵🇹', code: '+351' },
  { key: 'US', label: 'Estados Unidos', flag: '🇺🇸', code: '+1' },
  { key: 'OTHER', label: 'Outro país', flag: '🌐', code: '' },
]

// ── Definição das 12 perguntas (uma por vez) ───────────────────────────────────
//
// Bloco 1 — Quem você é (perguntas 1–5):
//   1. Nome completo  (input)
//   2. WhatsApp com DDD  (whatsapp)
//   3. Email  (input)
//   4. Como você chegou até aqui? (textarea)
//   5. Você já viveu alguma experiência... (textarea)
// Bloco 2 — O que você carrega (perguntas 6–9): textareas
// Bloco 3 — Comprometimento e disponibilidade (perguntas 10–12): textareas
// Bloco 4 — O que você espera (perguntas 13–14): textareas
//
// Total: 14 perguntas. (3 novos campos nome/whatsapp/email + 11 antigas)
type FieldKey =
  | 'name'
  | 'whatsapp'
  | 'email'
  | 'q4' | 'q5'
  | 'q6' | 'q7' | 'q8' | 'q9'
  | 'q10' | 'q11' | 'q12'
  | 'q13' | 'q14'

interface QuestionDef {
  key: FieldKey
  label: string
  type: 'text' | 'email' | 'whatsapp' | 'textarea'
  blockTitle: string
  blockSubtitle: string
}

const QUESTIONS: QuestionDef[] = [
  // BLOCO 1 — Quem você é
  { key: 'name', type: 'text', blockTitle: 'BLOCO 1', blockSubtitle: 'Quem você é',
    label: 'Qual é o seu nome completo?' },
  { key: 'whatsapp', type: 'whatsapp', blockTitle: 'BLOCO 1', blockSubtitle: 'Quem você é',
    label: 'Qual é o seu WhatsApp com DDD?' },
  { key: 'email', type: 'email', blockTitle: 'BLOCO 1', blockSubtitle: 'Quem você é',
    label: 'Qual é o seu email?' },
  { key: 'q4', type: 'textarea', blockTitle: 'BLOCO 1', blockSubtitle: 'Quem você é',
    label: 'Como você chegou até aqui? O que te trouxe para este formulário agora?' },
  { key: 'q5', type: 'textarea', blockTitle: 'BLOCO 1', blockSubtitle: 'Quem você é',
    label: 'Você já viveu alguma experiência com o meu trabalho, Reative os 4 Elementos, Protocolo de Liberação e Cocriação, Mapa de Cura ou outro? Se sim, qual foi sua experiência?' },

  // BLOCO 2 — O que você carrega
  { key: 'q6', type: 'textarea', blockTitle: 'BLOCO 2', blockSubtitle: 'O que você carrega',
    label: 'O que você está buscando transformar de verdade? Não o sintoma, a raiz. O que você sente que ainda não se moveu por mais que você tenha tentado?' },
  { key: 'q7', type: 'textarea', blockTitle: 'BLOCO 2', blockSubtitle: 'O que você carrega',
    label: 'Há quanto tempo você carrega isso? Como esse padrão se manifesta na sua vida hoje?' },
  { key: 'q8', type: 'textarea', blockTitle: 'BLOCO 2', blockSubtitle: 'O que você carrega',
    label: 'O que você já tentou antes para resolver isso? O que funcionou e o que não funcionou?' },
  { key: 'q9', type: 'textarea', blockTitle: 'BLOCO 2', blockSubtitle: 'O que você carrega',
    label: 'Existe algo que você nunca contou a ninguém e que sente que ainda pesa no seu campo?' },

  // BLOCO 3 — Comprometimento e disponibilidade
  { key: 'q10', type: 'textarea', blockTitle: 'BLOCO 3', blockSubtitle: 'Comprometimento e disponibilidade',
    label: 'O Reacenda o Diamante é um processo de 126 dias com atendimentos individuais a cada 21 dias. Você tem disponibilidade real para esse comprometimento agora?' },
  { key: 'q11', type: 'textarea', blockTitle: 'BLOCO 3', blockSubtitle: 'Comprometimento e disponibilidade',
    label: 'Este é um processo de alto comprometimento e investimento significativo. Você está em um momento de vida em que pode priorizar sua transformação de tempo, energia e recursos?' },
  { key: 'q12', type: 'textarea', blockTitle: 'BLOCO 3', blockSubtitle: 'Comprometimento e disponibilidade',
    label: 'O que faria você desistir no meio do processo? Seja honesto.' },

  // BLOCO 4 — O que você espera
  { key: 'q13', type: 'textarea', blockTitle: 'BLOCO 4', blockSubtitle: 'O que você espera',
    label: 'Como seria sua vida ao final de 126 dias se esse padrão se dissolvesse completamente?' },
  { key: 'q14', type: 'textarea', blockTitle: 'BLOCO 4', blockSubtitle: 'O que você espera',
    label: 'O que você quer que eu saiba sobre você antes de ler suas respostas?' },
]

const TOTAL_STEPS = QUESTIONS.length

export default function PageDiamanteFormulario() {
  usePageMeta({
    title: 'Formulário de Qualificação Diamante — Reative os 4 Elementos',
    description: 'Formulário de qualificação para o processo Reacenda o Diamante — 126 dias de transformação individual.',
    ogImage: 'https://static.lumi.new/64/6414888ed545dfded663c31c5466e840.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/diamante/formulario/',
  })

  // ── Estado dos campos ────────────────────────────────────────────────────────
  const [step, setStep] = useState(1) // 1..TOTAL_STEPS
  const [direction, setDirection] = useState(1)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')

  // WhatsApp
  const [waCountry, setWaCountry] = useState<CountryKey>('BR')
  const [waCustomCode, setWaCustomCode] = useState('')
  const [waNumber, setWaNumber] = useState('')
  const [waOpen, setWaOpen] = useState(false)
  const waDropdownRef = useRef<HTMLDivElement>(null)

  // Respostas longas
  const [answers, setAnswers] = useState<Record<string, string>>({
    q4: '', q5: '', q6: '', q7: '', q8: '', q9: '',
    q10: '', q11: '', q12: '', q13: '', q14: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // ── Helpers do WhatsApp ──────────────────────────────────────────────────────
  const waActiveCountry = COUNTRIES.find((c) => c.key === waCountry)!
  const waEffectiveCode = waCountry === 'OTHER' ? waCustomCode.trim() : waActiveCountry.code
  const waDigits = waNumber.replace(/\D/g, '')
  const waMinDigits = waCountry === 'BR' ? 10 : 7
  const waCustomCodeValid = waCountry !== 'OTHER' || /^\+\d{1,4}$/.test(waCustomCode.trim())

  const formatBrazil = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 2) return d
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  }
  const formatIntl = (v: string) => v.replace(/\D/g, '').slice(0, 20)

  const handleWaNumberChange = (v: string) => {
    if (waCountry === 'BR') setWaNumber(formatBrazil(v))
    else setWaNumber(formatIntl(v))
  }
  const handleWaCountryChange = (next: CountryKey) => {
    setWaCountry(next)
    if (next === 'BR') setWaNumber((prev) => formatBrazil(prev))
    else setWaNumber((prev) => formatIntl(prev))
  }
  const handleWaCustomCodeChange = (v: string) => {
    let clean = v.replace(/[^\d+]/g, '')
    if (!clean.startsWith('+')) clean = '+' + clean.replace(/\+/g, '')
    clean = '+' + clean.slice(1).replace(/\+/g, '').slice(0, 4)
    setWaCustomCode(clean)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (waDropdownRef.current && !waDropdownRef.current.contains(e.target as Node)) {
        setWaOpen(false)
      }
    }
    if (waOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [waOpen])

  // ── Validação ────────────────────────────────────────────────────────────────
  function validateStep(s: number): Record<string, string> {
    const q = QUESTIONS[s - 1]
    const e: Record<string, string> = {}
    if (!q) return e

    if (q.key === 'name') {
      if (!name.trim()) e.name = 'Este campo é obrigatório. Por favor informe seu nome completo.'
    } else if (q.key === 'whatsapp') {
      if (waDigits.length < waMinDigits || !waCustomCodeValid) {
        e.whatsapp = 'Este campo é obrigatório. Por favor informe seu WhatsApp com DDD.'
      }
    } else if (q.key === 'email') {
      if (!email.trim()) e.email = 'Este campo é obrigatório. Por favor informe seu email.'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Por favor informe um email válido.'
    } else {
      if (!answers[q.key]?.trim()) {
        e[q.key] = 'Este campo é obrigatório. Por favor responda com honestidade.'
      }
    }
    return e
  }

  function handleContinue() {
    const errs = validateStep(step)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    setDirection(1)
    setStep((s) => s + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function handleBack() {
    setErrors({})
    setDirection(-1)
    setStep((s) => Math.max(1, s - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function handleFinalSubmit() {
    const errs = validateStep(TOTAL_STEPS)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    setSubmitting(true)
    setSubmitError('')
    try {
      const fullWhatsapp = `${waEffectiveCode} ${waNumber.trim()}`.trim()
      const now = new Date().toISOString()
      await lumi.entities.diamante_formulario.create({
        name: name.trim(),
        whatsapp: fullWhatsapp,
        email: email.trim(),
        ...answers,
        createdAt: now,
        updatedAt: now,
      })
      setSubmitted(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Erro ao enviar formulário Diamante:', err)
      setSubmitError('Ocorreu um erro ao enviar suas respostas. Por favor, tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (step < TOTAL_STEPS) handleContinue()
    else handleFinalSubmit()
  }

  // ── Render helpers ───────────────────────────────────────────────────────────
  const currentQ = QUESTIONS[step - 1]
  const isLast = step === TOTAL_STEPS
  const buttonLabel = isLast
    ? (submitting ? 'Enviando...' : 'Enviar minhas respostas')
    : 'Continuar'
  const progressPct = (step / TOTAL_STEPS) * 100

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
    center: { x: 0, opacity: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
    exit: (dir: number) => ({ x: dir > 0 ? -48 : 48, opacity: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } }),
  }

  return (
    <div className="min-h-screen w-full font-body" style={{ background: '#0a0f14', color: 'var(--ink, #e8e0d5)' }}>

      {/* Header */}
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Mônica Maria
        </p>
      </header>

      <main className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-14">

        {submitted ? (
          /* ── CONFIRMAÇÃO ── */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="text-center py-16 md:py-24 max-w-xl mx-auto"
          >
            <div className="flex justify-center mb-8">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <polygon
                  points="24,4 44,18 24,44 4,18"
                  fill="none"
                  stroke={GOLD}
                  strokeWidth="1.5"
                  opacity="0.7"
                />
                <polygon points="24,4 44,18 24,44 4,18" fill={`${GOLD}14`} />
                <polygon
                  points="24,10 38,19 24,38 10,19"
                  fill="none"
                  stroke={GOLD_SOFT}
                  strokeWidth="0.8"
                  opacity="0.4"
                />
              </svg>
            </div>
            <div
              className="h-px w-16 mx-auto mb-8"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}66, transparent)` }}
            />
            <p
              className="text-[10px] tracking-[0.4em] uppercase font-body mb-6"
              style={{ color: GOLD_SOFT }}
            >
              Reacenda o Diamante
            </p>
            <h2 className="font-display type-hero text-[var(--ink,#e8e0d5)] mb-8 leading-snug">
              Suas respostas{' '}
              <em className="italic font-light" style={{ color: GOLD_SOFT }}>chegaram.</em>
            </h2>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.75)' }}>
              Obrigada por compartilhar com tanta profundidade e coragem. Lerei tudo com cuidado e entrarei em contato em até 72 horas.
            </p>
            <p className="font-display italic mt-6 text-[18px]" style={{ color: GOLD_SOFT }}>
              Com amor e presença,<br />Mônica — Reacenda Luz
            </p>
          </motion.div>
        ) : (
          <>
            {/* ── INTRO ── */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-10"
            >
              <div
                className="h-px w-16 mx-auto mb-7"
                style={{ background: `linear-gradient(90deg, transparent, ${GOLD}66, transparent)` }}
              />
              <p
                className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
                style={{ color: GOLD_SOFT }}
              >
                Formulário de qualificação · Reacenda o Diamante
              </p>
              <p className="font-display italic type-subtitle text-[var(--ink,#e8e0d5)] mb-5 max-w-xl mx-auto leading-relaxed">
                Decidir avançar é o primeiro passo para a mudança e que magnífico o seu interesse no Reacenda o Diamante.
              </p>
              <p className="font-body type-body max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(232,224,213,0.65)' }}>
                Este formulário é o primeiro passo da sua jornada de transformação. Responda com honestidade e profundidade. Suas respostas me ajudam a entender se este é o momento certo para você e se tenho como te acompanhar da forma que você merece.
              </p>
            </motion.div>

            {/* ── BARRA DE PROGRESSO ── */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] tracking-[0.3em] uppercase font-body" style={{ color: GOLD_SOFT }}>
                  Pergunta <span className="numerals">{step}</span> de <span className="numerals">{TOTAL_STEPS}</span>
                </p>
                <p className="text-[11px] tracking-[0.2em] uppercase font-body opacity-50" style={{ color: GOLD_SOFT }}>
                  <span className="numerals">{Math.round(progressPct)}</span>%
                </p>
              </div>

              <div
                className="w-full h-[3px] rounded-full overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.07)' }}
              >
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: `linear-gradient(90deg, ${GOLD}99, ${GOLD_SOFT})`,
                    boxShadow: `0 0 8px ${GOLD}55`,
                  }}
                  initial={false}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>

            {/* ── PERGUNTA ATUAL (animada, uma por vez) ── */}
            <div className="relative overflow-hidden" style={{ minHeight: '320px' }}>
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={`step-${step}`}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  <form onSubmit={handleFormSubmit} noValidate>
                    {/* Block header */}
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className="text-[10px] tracking-[0.4em] uppercase font-body px-3 py-1 rounded-full"
                          style={{
                            color: ACCENT_SOFT,
                            border: `1px solid ${ACCENT_SOFT}44`,
                            background: `${ACCENT}12`,
                          }}
                        >
                          {currentQ.blockTitle}
                        </span>
                        <div
                          className="flex-1 h-px"
                          style={{ background: `linear-gradient(90deg, ${ACCENT_SOFT}33, transparent)` }}
                        />
                      </div>
                      <p
                        className="font-display italic text-[18px] md:text-[20px]"
                        style={{ color: `${ACCENT_SOFT}bb` }}
                      >
                        {currentQ.blockSubtitle}
                      </p>
                    </div>

                    {/* Pergunta */}
                    <div className="mb-8">
                      <label
                        htmlFor={currentQ.key}
                        className="block font-body type-body leading-relaxed mb-3"
                        style={{ color: 'rgba(232,224,213,0.9)' }}
                      >
                        <span
                          className="numerals inline-block w-7 h-7 rounded-full text-[13px] text-center leading-7 mr-2 flex-shrink-0"
                          style={{
                            color: ACCENT_SOFT,
                            border: `1px solid ${ACCENT_SOFT}44`,
                            background: `${ACCENT}12`,
                            verticalAlign: 'middle',
                          }}
                        >
                          {step}
                        </span>
                        {currentQ.label}
                      </label>

                      {/* Campo: Nome (text) */}
                      {currentQ.type === 'text' && (
                        <>
                          <input
                            id={currentQ.key}
                            type="text"
                            value={name}
                            autoFocus
                            onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })) }}
                            placeholder="Seu nome completo"
                            className="w-full rounded-sm px-4 py-4 font-body type-body transition-all duration-200 focus:outline-none"
                            style={{
                              background: errors.name ? 'rgba(220,80,80,0.05)' : 'rgba(255,255,255,0.04)',
                              border: `1px solid ${errors.name ? 'rgba(220,80,80,0.45)' : 'rgba(255,255,255,0.1)'}`,
                              color: 'rgba(232,224,213,0.92)',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = errors.name ? 'rgba(220,80,80,0.6)' : `${ACCENT}66`
                              e.currentTarget.style.background = errors.name ? 'rgba(220,80,80,0.07)' : 'rgba(255,255,255,0.07)'
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = errors.name ? 'rgba(220,80,80,0.45)' : 'rgba(255,255,255,0.1)'
                              e.currentTarget.style.background = errors.name ? 'rgba(220,80,80,0.05)' : 'rgba(255,255,255,0.04)'
                            }}
                          />
                          {errors.name && (
                            <p className="mt-2 font-body text-[13px] italic" style={{ color: 'rgba(220,80,80,0.9)' }}>
                              {errors.name}
                            </p>
                          )}
                        </>
                      )}

                      {/* Campo: Email */}
                      {currentQ.type === 'email' && (
                        <>
                          <input
                            id={currentQ.key}
                            type="email"
                            value={email}
                            autoFocus
                            onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: '' })) }}
                            placeholder="seu@email.com"
                            className="w-full rounded-sm px-4 py-4 font-body type-body transition-all duration-200 focus:outline-none"
                            style={{
                              background: errors.email ? 'rgba(220,80,80,0.05)' : 'rgba(255,255,255,0.04)',
                              border: `1px solid ${errors.email ? 'rgba(220,80,80,0.45)' : 'rgba(255,255,255,0.1)'}`,
                              color: 'rgba(232,224,213,0.92)',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = errors.email ? 'rgba(220,80,80,0.6)' : `${ACCENT}66`
                              e.currentTarget.style.background = errors.email ? 'rgba(220,80,80,0.07)' : 'rgba(255,255,255,0.07)'
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = errors.email ? 'rgba(220,80,80,0.45)' : 'rgba(255,255,255,0.1)'
                              e.currentTarget.style.background = errors.email ? 'rgba(220,80,80,0.05)' : 'rgba(255,255,255,0.04)'
                            }}
                          />
                          {errors.email && (
                            <p className="mt-2 font-body text-[13px] italic" style={{ color: 'rgba(220,80,80,0.9)' }}>
                              {errors.email}
                            </p>
                          )}
                        </>
                      )}

                      {/* Campo: WhatsApp (mesmo padrão da home / triagem) */}
                      {currentQ.type === 'whatsapp' && (
                        <>
                          <div
                            className={`relative flex items-stretch border rounded-sm transition-colors ${
                              waNumber.trim()
                                ? 'border-[rgba(255,255,255,0.25)] bg-[rgba(255,255,255,0.08)]'
                                : errors.whatsapp
                                ? 'border-[rgba(220,80,80,0.5)] bg-[rgba(220,80,80,0.04)]'
                                : 'border-[rgba(255,255,255,0.15)] bg-[rgba(255,255,255,0.05)]'
                            } focus-within:border-[rgba(255,255,255,0.4)] focus-within:shadow-[0_0_0_2px_rgba(255,255,255,0.08)]`}
                            ref={waDropdownRef}
                          >
                            <button
                              type="button"
                              onClick={() => setWaOpen((v) => !v)}
                              aria-haspopup="listbox"
                              aria-expanded={waOpen}
                              className="flex items-center gap-1.5 px-3 py-4 text-[15px] font-body text-[var(--ink-soft)] bg-[var(--ink)]/[0.03] border-r border-[var(--line)] hover:text-[var(--ink)] transition-colors flex-shrink-0"
                            >
                              <span className="text-[15px] leading-none">{waActiveCountry.flag}</span>
                              <span className="tracking-wider numerals text-[13px]">{waEffectiveCode || '+—'}</span>
                              <svg
                                className={`w-3 h-3 text-[var(--ink-soft)] transition-transform duration-200 ${waOpen ? 'rotate-180' : ''}`}
                                viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
                              >
                                <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>

                            <input
                              id={currentQ.key}
                              type="tel"
                              inputMode="numeric"
                              autoFocus
                              value={waNumber}
                              onChange={(e) => { handleWaNumberChange(e.target.value); if (errors.whatsapp) setErrors((p) => ({ ...p, whatsapp: '' })) }}
                              placeholder="número com DDD"
                              className="flex-1 min-w-0 bg-transparent px-3 sm:px-4 py-4 text-[16px] font-body outline-none text-[rgba(255,255,255,0.9)] placeholder:text-[var(--ink-soft)]"
                            />

                            {waOpen && (
                              <div
                                role="listbox"
                                className="absolute left-0 top-full mt-1 w-full sm:w-[280px] overflow-hidden"
                                style={{
                                  backgroundColor: '#1a1a24',
                                  border: '1px solid rgba(255,255,255,0.12)',
                                  borderRadius: '8px',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                  zIndex: 9999,
                                }}
                              >
                                {COUNTRIES.map((c) => {
                                  const active = waCountry === c.key
                                  return (
                                    <button
                                      key={c.key}
                                      type="button"
                                      role="option"
                                      aria-selected={active}
                                      onClick={() => { handleWaCountryChange(c.key); setWaOpen(false) }}
                                      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,1)' } }}
                                      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)' } }}
                                      className="w-full flex items-center gap-2.5 text-left font-body transition-colors"
                                      style={{
                                        padding: '10px 16px', fontSize: '14px', lineHeight: 1.6,
                                        color: active ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.85)',
                                        backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                                      }}
                                    >
                                      <span className="leading-none flex-shrink-0" style={{ fontSize: '16px' }}>{c.flag}</span>
                                      {c.code
                                        ? <span className="numerals tracking-wider flex-shrink-0 min-w-[44px]">{c.code}</span>
                                        : <span className="tracking-wider flex-shrink-0 min-w-[44px]">—</span>}
                                      <span className="truncate">{c.label}</span>
                                    </button>
                                  )
                                })}
                              </div>
                            )}
                          </div>

                          {waCountry === 'OTHER' && (
                            <input
                              type="text"
                              inputMode="tel"
                              value={waCustomCode}
                              onChange={(e) => handleWaCustomCodeChange(e.target.value)}
                              placeholder="+00"
                              className="input-field w-full px-4 py-3 text-[15px] font-body rounded-sm mt-2"
                            />
                          )}
                          {errors.whatsapp && (
                            <p className="mt-2 font-body text-[13px] italic" style={{ color: 'rgba(220,80,80,0.9)' }}>
                              {errors.whatsapp}
                            </p>
                          )}
                        </>
                      )}

                      {/* Campo: Textarea (perguntas longas) */}
                      {currentQ.type === 'textarea' && (
                        <>
                          <textarea
                            id={currentQ.key}
                            value={answers[currentQ.key] ?? ''}
                            autoFocus
                            onChange={(e) => {
                              const v = e.target.value
                              setAnswers((p) => ({ ...p, [currentQ.key]: v }))
                              if (errors[currentQ.key]) setErrors((p) => ({ ...p, [currentQ.key]: '' }))
                            }}
                            rows={5}
                            placeholder="Escreva livremente..."
                            className="w-full rounded-sm px-4 py-4 font-body type-body resize-y transition-all duration-200 focus:outline-none"
                            style={{
                              background: errors[currentQ.key] ? 'rgba(220,80,80,0.05)' : 'rgba(255,255,255,0.04)',
                              border: `1px solid ${errors[currentQ.key] ? 'rgba(220,80,80,0.45)' : 'rgba(255,255,255,0.1)'}`,
                              color: 'rgba(232,224,213,0.92)',
                              minHeight: '130px',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = errors[currentQ.key] ? 'rgba(220,80,80,0.6)' : `${ACCENT}66`
                              e.currentTarget.style.background = errors[currentQ.key] ? 'rgba(220,80,80,0.07)' : 'rgba(255,255,255,0.07)'
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = errors[currentQ.key] ? 'rgba(220,80,80,0.45)' : 'rgba(255,255,255,0.1)'
                              e.currentTarget.style.background = errors[currentQ.key] ? 'rgba(220,80,80,0.05)' : 'rgba(255,255,255,0.04)'
                            }}
                          />
                          {errors[currentQ.key] && (
                            <p className="mt-2 font-body text-[13px] italic" style={{ color: 'rgba(220,80,80,0.9)' }}>
                              {errors[currentQ.key]}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Submit error */}
                    {submitError && (
                      <p className="mb-6 text-center font-body text-[14px] italic" style={{ color: 'rgba(220,80,80,0.9)' }}>
                        {submitError}
                      </p>
                    )}

                    {/* Separator */}
                    <div
                      className="h-px w-full mb-8"
                      style={{ background: `linear-gradient(90deg, transparent, ${GOLD}44, transparent)` }}
                    />

                    {/* Botão de ação */}
                    {isLast ? (
                      <button
                        type="submit"
                        disabled={submitting}
                        className="block w-full text-center py-5 min-h-[60px] rounded-sm font-body type-button tracking-[0.15em] uppercase transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{
                          background: submitting
                            ? `${GOLD}18`
                            : `linear-gradient(135deg, ${GOLD}22, ${GOLD}14)`,
                          border: `1px solid ${GOLD}55`,
                          color: GOLD_SOFT,
                        }}
                        onMouseEnter={(e) => {
                          if (!submitting) {
                            e.currentTarget.style.background = `linear-gradient(135deg, ${GOLD}33, ${GOLD}22)`
                            e.currentTarget.style.borderColor = `${GOLD}88`
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!submitting) {
                            e.currentTarget.style.background = `linear-gradient(135deg, ${GOLD}22, ${GOLD}14)`
                            e.currentTarget.style.borderColor = `${GOLD}55`
                          }
                        }}
                      >
                        {buttonLabel}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="block w-full text-center py-5 min-h-[60px] rounded-sm font-body type-button tracking-[0.15em] uppercase transition-all duration-300 active:scale-[0.98]"
                        style={{
                          background: `linear-gradient(135deg, ${ACCENT}22, ${ACCENT}14)`,
                          border: `1px solid ${ACCENT}55`,
                          color: ACCENT_SOFT,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `linear-gradient(135deg, ${ACCENT}33, ${ACCENT}22)`
                          e.currentTarget.style.borderColor = `${ACCENT}88`
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `linear-gradient(135deg, ${ACCENT}22, ${ACCENT}14)`
                          e.currentTarget.style.borderColor = `${ACCENT}55`
                        }}
                      >
                        {buttonLabel}
                      </button>
                    )}

                    {/* Voltar */}
                    {step > 1 && !submitting && (
                      <button
                        type="button"
                        onClick={handleBack}
                        className="w-full mt-4 px-6 py-3 font-body text-[13px] tracking-[0.2em] uppercase opacity-50 hover:opacity-80 transition-opacity"
                        style={{ color: ACCENT_SOFT }}
                      >
                        ← Voltar
                      </button>
                    )}
                  </form>
                </motion.div>
              </AnimatePresence>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer
        className="w-full px-4 py-6 text-center border-t mt-8"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[10px] tracking-[0.3em] uppercase font-body opacity-40" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Mônica Maria
        </p>
      </footer>
    </div>
  )
}
